import type { RefObject } from 'preact';
import { useCallback, useLayoutEffect, useState, useRef, StateUpdater } from 'preact/hooks';
import { useResizeObserver } from '../hooks/UNSAFE_useResizeObserver';
import { Size } from '../utils/UNSAFE_size';
import { getTabWidths } from '../utils/PRIVATE_tabBarUtils';
import type { TrailingContentInfo } from '../utils/PRIVATE_tabBarUtils';

type ProgressiveTruncationProps = {
  rootRef: RefObject<HTMLDivElement>;
  isDisabled?: boolean;
};

export const useProgressiveTruncation = <K extends string | number>({
  rootRef,
  isDisabled = false
}: ProgressiveTruncationProps) => {
  const trailingContentWidths = useRef(new Map<K, TrailingContentInfo>());
  const [tabMaxWidth, setTabMaxWidth] = useState<Map<K, Size>>();
  const [tabBarWidth, setTabBarWidth] = useState<number>(0);

  useLayoutEffect(() => {
    if (tabMaxWidth == undefined && rootRef && rootRef.current && !isDisabled) {
      const isOverflow = tabBarWidth < rootRef.current.scrollWidth;
      if (!isOverflow) {
        return;
      }
      const tabWidths = getTabWidths(
        rootRef.current,
        trailingContentWidths,
        isDisabled,
        tabBarWidth,
        false
      );

      const tabMaxWidthMap = new Map(
        tabWidths.map((tab) => [tab.key, (tab.maxWidth + 'px') as Size])
      );
      setTabMaxWidth(tabMaxWidthMap);
    }
  }, [tabBarWidth, tabMaxWidth, isDisabled, rootRef]);

  const getTabBarMaxWidth = useCallback(
    (entry: { contentRect: { width: StateUpdater<number> } }) => {
      if (tabBarWidth !== entry.contentRect.width) {
        setTabBarWidth(entry.contentRect.width);
        if (tabMaxWidth !== undefined) {
          setTabMaxWidth(undefined);
        }
      }
    },
    [tabMaxWidth, tabBarWidth]
  );
  useResizeObserver(rootRef, getTabBarMaxWidth, { isDisabled: isDisabled });
  return {
    maxWidths: tabMaxWidth
  };
};
